/**
 * ⟦C57 · lot 5d · A-174⟧ **Les textes de « Nouvelle vidéo », en deux longueurs.**
 *
 * Trois sections, un pied : à l'écran, des titres de 5 mots au plus, les chiffres qui comptent et des
 * phrases de 12 mots au plus. Ce que l'écran disait de plus (le principe de la production value, ce
 * qu'est l'équipe embauchée, l'offre qui a tourné, l'impasse du compte) est gardé mot pour mot et
 * vit derrière « 📰 Les dessous ». Pur : pas de DOM ; `textes-courts.test.ts` mesure les textes courts
 * avec le mètre de l'écran.
 */

import { nombre } from "../../game/format";

/** Le titre de la section sujet : le format se lit sur l'onglet et sur les post-it, plus besoin d'une phrase. */
export const TITRE_SUJET = "2. Le sujet";
/** Le titre de la section production value : l'argent dont on dispose est dans le compteur à côté. */
export const TITRE_PRODUCTION_VALUE = "3. Production value";
/** Le titre du bloc équipe. */
export const TITRE_EQUIPE = "L'équipe de cette vidéo";

/** La case de l'evergreen : tout y tient en une ligne. */
export const LIBELLE_EVERGREEN = "Viser l'intemporel : +50 % de temps et d'énergie, +1 idée consommée";

/**
 * Plus aucune idée en main : tout y tient en une phrase.
 *
 * ⟦C62 · lot événements · A-179⟧ La durée de la veille se LIT dans le moteur (`heuresVeille`, le même `actionCost`
 * que le bouton) : la phrase disait « veille (2 h) » quand le bouton en annonçait 4. Un seul mot : « idée ».
 */
export function plusDIdees(veilleOuverte: boolean, heuresVeille: number): string {
  return veilleOuverte
    ? `Plus d'idée : veille (${nombre(heuresVeille)} h) ou vis ta vie.`
    : "Plus d'idée : dors, demain est un autre jour.";
}

/**
 * ⟦C62 · lot événements · A-179⟧ **La raison écrite sur le bouton grisé.** Sans idée en main, « Lancer la
 * production » ne peut rien lancer : il le dit sur lui-même (« choisis un sujet » n'avait aucun sens devant une
 * liste vide), avec la porte de sortie : la veille quand elle est ouverte, la nuit sinon.
 */
export function raisonSansIdee(veilleOuverte: boolean): string {
  return veilleOuverte ? "plus d'idée : fais une veille" : "plus d'idée : dors d'abord";
}

/** Le principe de la production value, en une phrase. Le principe entier est dans les dessous. */
export const PRODUCTION_VALUE_COURT = "Payer ne donne aucun point : ça relève ton plafond.";
/** Le principe de la production value (version d'avant le chantier). */
export const PRODUCTION_VALUE_LONG =
  "Payer avant de tourner ne te donne aucun point : ça relève le plafond jusqu'où ta chaîne exploite pleinement ton geste sur cette vidéo.";

/** Le plein tarif et, quand il est plus bas, le maximum que le joueur peut payer. */
export function pleinTarifCourt(o: { plein: number; max: number }): string {
  return o.max < o.plein
    ? `Plein tarif : ${nombre(o.plein)} ₵ · ton max : ${nombre(o.max)} ₵.`
    : `Plein tarif : ${nombre(o.plein)} ₵.`;
}
/** Le plein tarif (version d'avant le chantier). */
export function pleinTarifLong(o: { plein: number; max: number }): string {
  return (
    `Plein tarif : ${nombre(o.plein)} ₵.` +
    (o.max < o.plein ? ` Tu ne peux pas dépasser ${nombre(o.max)} ₵ : c'est tout ce que tu as.` : "")
  );
}

/** Ce que veut dire « équipe embauchée » (le compteur d'avant le titre). */
export const EQUIPE_LONG = "Embauchée au marché — tu ne joues pas ces étapes.";

/** Une offre a tourné depuis le passage au marché. */
export function offrePerimeeCourt(noms: readonly string[]): string {
  return `⏳ L'offre a tourné : ${noms.join(", ")} ${noms.length > 1 ? "indisponibles" : "indisponible"}.`;
}
export function offrePerimeeLong(noms: readonly string[]): string {
  const plusieurs = noms.length > 1;
  return (
    `⏳ L'offre a tourné depuis ton passage au marché : ${noms.join(", ")} ` +
    `${plusieurs ? "ne sont" : "n'est"} plus disponible${plusieurs ? "s" : ""} — tu joueras ` +
    `${plusieurs ? "ces étapes" : "cette étape"} toi-même, ou repasse au marché.`
  );
}

/** Pied de page : il manque des idées pour ce format. */
export function ideesRequises(n: number): string {
  return `Il te faut ${n} idées : va en chercher.`;
}

/** Publier depuis un compte : l'impasse, en un titre et une phrase. */
export const IMPASSE_TITRE = "⛔ Publication pas branchée";
export const IMPASSE_COURT = "Joue en bac à sable, dans les réglages.";
export const IMPASSE_LONG = [
  "Publier depuis un compte n'est pas branché ici. Le serveur refuse — à raison — tout score calculé par le navigateur : il rejoue lui-même le pattern de ce tour et recalcule les cinq notes depuis tes gestes. Sur ce client, l'Atelier ne sait pas encore aller chercher ce pattern.",
  "En attendant, la production complète se joue en bac à sable (réglages → où la partie est calculée). Ta run de compte, elle, ne bouge pas.",
] as const;
